export interface PerformanceScope {
  campaignId: string | null;
  adsetId: string | null;
}

/**
 * The API declares `campaignId` as a UUID and rejects anything else with a 400, so a
 * hand-edited or truncated link has to be discarded here rather than forwarded. Sending it
 * on would surface a shared bad link as "could not load this project's performance" with a
 * retry that can never succeed, instead of the project-wide view the reader expected.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * No view applies an ad-set narrowing to anything it requests or filters, so `adset` is
 * parsed as absent: honouring it would render a breadcrumb level whose numbers are still
 * the whole campaign's. Restore the `params.get('adset')` read here once a query consumes
 * `scope.adsetId`, and the ad-set crumb below it starts working again.
 *
 * An ad set is only meaningful inside a campaign, so an ad-set parameter without one would
 * be discarded regardless.
 */
export function parseScope(params: URLSearchParams): PerformanceScope {
  const campaignId = params.get('campaign');
  if (!campaignId || !UUID.test(campaignId)) return { campaignId: null, adsetId: null };
  return { campaignId, adsetId: null };
}

/**
 * The inverse. An ad set without a campaign is unrepresentable (see `parseScope`), so
 * clearing the campaign here drops the ad set too rather than leaving a dangling `adset`
 * param that the next `parseScope` would discard anyway.
 */
export function toScopeParams(scope: PerformanceScope): Record<string, string> {
  if (!scope.campaignId) return {};
  return scope.adsetId
    ? { campaign: scope.campaignId, adset: scope.adsetId }
    : { campaign: scope.campaignId };
}
